import React, { useMemo } from "react";
import { getVal, setVal, UI_CAPTION_DF, UI_CAPTION_BOLD_DF } from "oolib";

import TooltipV2 from "@/components/UI/Tooltip";
import { useMetricCardConfig } from "./ConfigForm_Context";
import { ConfigForm_SectionWrapper } from "./ConfigForm_SectionWrapper";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { TitleInputWithDefault } from "../../utils/TitleInputWithDefault";
import { normalizeToArray } from "../index";
import { CollapseTransition } from "../../utils/CollapseTransition";


const STAT_FUNCTION_OPTIONS = [
  {
    display: "Descriptive",
    options: [
      // TODO: enable when design guidelines are finalized
      // { display: "Entries/Empty", value: "count", desc: "Count of non-null values" },
      { display: "Sum", value: "sum", desc: "Sum of all values" },
      { display: "Average", value: "avg", desc: "Mean of all values" },
      { display: "Median", value: "median", desc: "Middle value" },
      { display: "Mode", value: "mode", desc: "Most frequent value" },
      { display: "Minimum", value: "min", desc: "Smallest value" },
      { display: "Maximum", value: "max", desc: "Largest value" },
      { display: "Range", value: "range", desc: "Difference between max and min" }
    ]
  },
  {
    display: "Distribution & Shape",
    options: [
      { display: "Standard Deviation", value: "stddev", desc: "Measure of spread" },
      { display: "Percentiles", value: "percentiles", desc: "25th, 50th, 75th percentiles" },
      { display: "Outlier Count", value: "outlierCount", desc: "Number of outliers (IQR method)" },
      { display: "Outlier %", value: "outlierPercent", desc: "Percentage of outliers" },
      { display: "Unique values count", value: "uniqueCount", desc: "Count of distinct values" }
    ]
  }
];

// For non-numeric (categorical) fields, only show Descriptive options
const CATEGORICAL_STAT_FUNCTION_OPTIONS = STAT_FUNCTION_OPTIONS.filter(
  group => group.display === "Descriptive"
);


const AGGREGATE_TOOLTIP = {
  placement: 'right',
  primaryContent: {
    layout: 'custom',
    render: (
      <div style={{ padding: '0.8rem', maxWidth: '30rem' }}>
        <UI_CAPTION_DF style={{ color: 'inherit' }}>
          The selected number fields are combined into one series. Choose how they should be aggregated (e.g., sum, subtract, custom formula).
        </UI_CAPTION_DF>
      </div>
    ),
  },
};

const STAT_FUNCTION_TOOLTIP = {
  placement: 'right',
  primaryContent: {
    layout: 'custom',
    render: (
      <div style={{ padding: '0.8rem', maxWidth: '30rem' }}>
        <UI_CAPTION_DF style={{ color: 'inherit' }}>
          The aggregated series is then processed through a function (e.g., average, total, max) to produce the final metric value displayed on the card.
        </UI_CAPTION_DF>
      </div>
    ),
  },
};

export const ConfigForm_ConfigureSection = () => {
  const {
    widgetConfig,
    updateWidget,
    dataKeys,
    measureMode,
    arithmeticState,
    effectiveSelectedCount,
  } = useMetricCardConfig();

  const selectedField = getVal(widgetConfig, dataKeys.field);
  const isNumberInput = selectedField?.comp === 'NumberInput';

  // --- Arithmetic state (shared with DataSection via context) ---
  const {
    hasMultipleFields,
    aggregateType,
    aggregateOptions,
    customExpression,
    validationError,
    handleAggregateChange,
    handleCustomExpressionChange
  } = arithmeticState;

  // Resolve current aggregate value for DataFieldInputSelector
  const aggregateValue = useMemo(() => {
    const match = aggregateOptions.find(o => o.value === aggregateType);
    if (!match) return null;
    return { ...match, dataType: "Aggregate" };
  }, [aggregateOptions, aggregateType]);

  // Resolve current stat function value for DataFieldInputSelector
  const statFunctionValue = useMemo(() => {
    const currentStat = getVal(widgetConfig, dataKeys.statFunction);
    if (!currentStat) return null;
    const match = STAT_FUNCTION_OPTIONS
      .flatMap(group => group.options)
      .find(opt => opt.value === currentStat);
    if (!match) return null;
    return { ...match, dataType: "Function" };
  }, [widgetConfig, dataKeys.statFunction]);

  // Resolve current categorical stat function value
  const categoricalStatValue = useMemo(() => {
    const currentStat = getVal(widgetConfig, dataKeys.categoricalStatFunction);
    const match = CATEGORICAL_STAT_FUNCTION_OPTIONS
      .flatMap(group => group.options)
      .find(opt => opt.value === (currentStat || 'sum'));
    if (!match) return null;
    return { ...match, dataType: "Function" };
  }, [widgetConfig, dataKeys.categoricalStatFunction]);

  return (
    <ConfigForm_SectionWrapper icon="SlidersHorizontal" title="Configure Data">

      {/* Aggregate dropdown + custom expression — only when multiple NumberInput fields */}
      <CollapseTransition isVisible={isNumberInput && hasMultipleFields} id="aggregate-section">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <DataFieldInputSelector
            id="aggregate-type"
            options={aggregateOptions}
            value={aggregateValue}
            onChange={(id, v) => handleAggregateChange(id, v)}
            placeholder="Select Aggregate"
            placeholderIcon="MathOperations"
            trailingIcon="CaretRight"
            tooltip={aggregateValue ? AGGREGATE_TOOLTIP : { text: "Choose how to combine multiple fields", placement: "top" }}
          />

          {/* Custom expression input — auto-applies with debounce */}
          <CollapseTransition isVisible={aggregateType === 'custom'} id="custom-expression">
            <TooltipV2 text="Enter a custom expression for the aggregation" placement="right" wrapperDisplay="block">
              <TitleInputWithDefault
                id="custom-expression"
                value={customExpression}
                onChange={handleCustomExpressionChange}
                placeholder="e.g., (A + B) - C"
                tagLabel="EXPRESSION"
                validationStatus={validationError ? { type: 'error', msg: validationError } : null}
              />
            </TooltipV2>
          </CollapseTransition>
        </div>
      </CollapseTransition>

      {/* Stat function — for numeric (NumberInput) fields */}
      <CollapseTransition isVisible={isNumberInput} id="stat-function">
        <DataFieldInputSelector
          id={dataKeys.statFunction}
          options={STAT_FUNCTION_OPTIONS}
          value={statFunctionValue}
          onChange={(id, v) => {
            updateWidget(w => setVal(w, dataKeys.statFunction, v.value));
          }}
          placeholder="Select Function"
          placeholderIcon="Function"
          trailingIcon="CaretRight"
          tooltip={statFunctionValue ? STAT_FUNCTION_TOOLTIP : { text: "Choose the statistical function to apply", placement: "top" }}
          hideSearchHeader
        />
      </CollapseTransition>

      {/* Categorical stat function — only when 2+ categorical values selected */}
      <CollapseTransition isVisible={!isNumberInput && effectiveSelectedCount > 1} id="categorical-stat">
        <DataFieldInputSelector
          id={dataKeys.categoricalStatFunction}
          options={CATEGORICAL_STAT_FUNCTION_OPTIONS}
          value={categoricalStatValue}
          onChange={(id, v) => {
            updateWidget(w => setVal(w, dataKeys.categoricalStatFunction, v.value));
          }}
          placeholder="Select Function"
          placeholderIcon="Function"
          trailingIcon="CaretRight"
          tooltip={{ text: "Choose the statistical function to apply", placement: "top" }}
          hideSearchHeader
        />
      </CollapseTransition>

    </ConfigForm_SectionWrapper>
  );
};
